import { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Non-dismissable legal consent modal.
 *
 * Shown automatically (via AuthContext) whenever the authenticated user doesn't
 * have a valid `legal_acceptance` for the current `legal_version`. Covers:
 *   - Existing users (pre-legal-gating era) on their next login.
 *   - Users created by Google OAuth (who never saw the register form).
 *   - Any user after a legal document version bump.
 */
export const LegalConsentModal = ({ onAccepted }) => {
  const { logout, updateUser } = useAuth();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [dataDeletion, setDataDeletion] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const all = terms && privacy && dataDeletion;

  const handleAccept = async () => {
    if (!all) return;
    setSubmitting(true);
    try {
      const res = await api.post('/auth/accept-legal', {
        accepted_terms: terms,
        accepted_privacy: privacy,
        accepted_data_deletion: dataDeletion,
      });
      toast.success('¡Gracias! Tu aceptación quedó registrada.');
      // Mark user as compliant locally so the modal closes instantly
      updateUser({
        requires_legal_acceptance: false,
        legal_acceptance: {
          version: res?.data?.legal_version,
          terms_accepted: true,
          privacy_accepted: true,
          data_deletion_accepted: true,
          accepted_at: res?.data?.accepted_at,
        },
      });
      if (onAccepted) {
        onAccepted();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar tu aceptación.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    // User declines → log them out. The app is not usable without acceptance.
    try {
      await logout();
    } finally {
      window.location.href = '/';
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="legal-consent-modal"
    >
      <div className="w-full max-w-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-white flex-shrink-0" weight="fill" />
          <div>
            <h2 className="text-lg font-bold text-white">Actualización legal requerida</h2>
            <p className="text-sm text-orange-50">
              Para continuar usando MaterMatch necesitamos tu consentimiento expreso.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
            De conformidad con la <strong>Ley Federal de Protección de Datos Personales en Posesión
            de los Particulares (LFPDPPP)</strong>, es indispensable que leas y aceptes los tres
            documentos siguientes antes de seguir usando la plataforma. Puedes abrir cada uno en una
            pestaña nueva y revisarlo con calma.
          </p>

          <div className="border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                data-testid="consent-terms-checkbox"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                He leído y acepto los{' '}
                <a
                  href="/api/legal/terms-conditions/page"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                  data-testid="consent-terms-link"
                >
                  Términos y Condiciones
                </a>
                .
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                data-testid="consent-privacy-checkbox"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                He leído y acepto el{' '}
                <a
                  href="/api/legal/privacy-policy/page"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                  data-testid="consent-privacy-link"
                >
                  Aviso de Privacidad
                </a>
                .
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={dataDeletion}
                onChange={(e) => setDataDeletion(e.target.checked)}
                className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                data-testid="consent-data-deletion-checkbox"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                He leído y acepto la{' '}
                <a
                  href="/api/legal/data-deletion/page"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                  data-testid="consent-data-deletion-link"
                >
                  Política de Eliminación de Cuenta
                </a>
                .
              </span>
            </label>
          </div>

          <p className="text-xs text-zinc-500 dark:text-zinc-400 italic">
            Tu aceptación queda registrada con la fecha y hora exactas. Podrás revocarla en cualquier
            momento solicitando la eliminación de tu cuenta desde{' '}
            <a href="mailto:contacto@matermatch.com" className="underline">contacto@matermatch.com</a>.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-800 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={handleReject}
            disabled={submitting}
            className="rounded-none text-zinc-600 dark:text-zinc-400"
            data-testid="consent-reject-btn"
          >
            Rechazar y cerrar sesión
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!all || submitting}
            className="rounded-none btn-primary h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="consent-accept-btn"
          >
            {submitting ? 'Guardando…' : 'Aceptar y continuar'}
            {!submitting && <ArrowRight className="w-5 h-5 ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LegalConsentModal;
